"use client";

/**
 * The Bash box, the one composer (founder 09-12: "keep the same composer on
 * every screen", "retire the super old composer for good now"; spec
 * 2026-09-12 one-composer). Born as the landing's summon composer made real
 * (founder round 08-26): the copper face leads the line; an example prompt
 * types itself until the field is focused; the control row carries attach ·
 * model · permissions · ↵ Delegate. The ENGINE was the dock's, piece for
 * piece (send lanes, focus law, prefill, attachments, prewarm), and the
 * dock's rich editor lives here now too.
 *
 * The app shell's Dock hosts it on every dock route, fixed at the bottom.
 * With the Line on (spec 2026-09-11) it is the type door: mounted while the
 * door is open, the ball takes Bash's seat as the mark (the way back to
 * voice), the draft is parked in the store so the door can close over the
 * words and open again on them, and a typed send rides the open line. With
 * the Line off it is the composer outright, the face as its mark.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useCreditStore } from "@/stores/creditStore";
import { type ImageAttachment, type FileAttachment } from "@bash/shared";
import { cn } from "@/lib/utils";
import { useLineOn, useShellSettled } from "@/lib/home-flags-hooks";
import { isDockWindow } from "@/lib/dock-window";
import { useTaskStore } from "@/stores/taskStore";
import { useComputerStore } from "@/stores/computerStore";
import { useComposerDraftStore } from "@/stores/composerDraftStore";
import { ModelSelector } from "@/components/ui/ModelSelector";
import { TaskAccessLevelPicker } from "@/components/settings/TaskAccessLevelPicker";
import { AttachmentStrip } from "@/components/message-input";
import { waitingForUpload } from "@/components/message-input/uploadCopy";
import { StagedQueue } from "@/components/message-input/StagedQueue";
import { useTaskScreenControlsFor } from "@/components/composer/useTaskScreenControls";
import { useComposerConverse } from "@/components/composer/useComposerConverse";
import { useComposerPrewarm } from "@/hooks/useComposerPrewarm";
import type { ConverseUiState } from "@/components/composer/useConverse";
import { useLine } from "@/components/composer/live/useLine";
import { Ball } from "@/components/composer/live/Ball";
import {
  liveBashRef,
  liveTalkRef,
  useLiveStore,
} from "@/components/composer/live/liveStore";
import { faceMoodFor, toneFor } from "@/components/composer/live/lineMachine";
import { BashFace } from "./BashFace";
import { AgentFace } from "@/components/team/AgentFace";
import { isAgentsV1 } from "@/lib/home-flags";
import { selectSelectedAgent, useTeamStore } from "@/stores/teamStore";
import { PillThread, moodFor } from "./PillThread";
import { usePillReceipt } from "./usePillReceipt";
import { THREAD_MS, usePillSeat } from "./usePillSeat";
import { rowsToAttachments, usePillFiles } from "./usePillFiles";
import { PILL_CHIP_CLASS, PILL_KEY_CLASS } from "./pillChrome";
import { usePillTaskMode } from "./pill-task/usePillTaskMode";
import { usePillTaskLock } from "./pill-task/usePillTaskLock";
import { usePillEditorPort } from "./pill-task/usePillEditorPort";
import {
  PillTaskPickers,
  type PillTaskPickersHandle,
} from "./pill-task/PillTaskPickers";
import { PillTaskKey } from "./pill-task/PillTaskKey";
import { DelegateFace } from "./pill-task/UploadingFace";
import { PillTryRow } from "./pill-task/PillTryRow";
import { PillHistory } from "./PillHistory";
import { deckDraws } from "@/components/composer/panels/drawn";
import { PanelDeck } from "@/components/composer/panels/PanelDeck";
import { undoRunApi } from "@/lib/composer/undo-client";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { usePillPanelDoors } from "./pillPanels";
import { createTypingLoop, EXAMPLE_PROMPTS } from "./typing";
import { PlusIcon } from "@/components/icons/PlusIcon";
import { ChevronLeftIcon } from "@/components/icons/ChevronLeftIcon";
import {
  RichEditor,
  type RichEditorHandle,
} from "@/components/home-briefing/rich-composer/RichEditor";
import { createHeightAnimator } from "@/components/home-briefing/rich-composer/height";
import { useDockRouter } from "@/components/composer/live/useDockRouter";
import "./bash-home.css";

/** The mark: the Dock's 32 px ball, in Bash's seat while the door is open. */
const MARK_SIZE = 32;

export function BashPill() {
  // In the shell's Dock window a push goes to the main window (useDockRouter).
  const router = useDockRouter();
  // The Line: the draft lives in the store while the pill is the door, so the
  // door can shut over the words and open again on them (the letter that
  // opened it is already there, liveStore.openDoor). Off, the pill's own state.
  // In the desktop shell the hydration commit still answers line-on (the
  // server's snapshot) and the client's answer lands a render later, so the
  // effects that act on it (a focus, a parked draft, parked files) wait for
  // the shell snapshot to settle; a client render is settled at once.
  const liveOn = useLineOn();
  const settled = useShellSettled();
  // TASK MODE (spec 2026-09-20 one-composer-everywhere §4): on a task's screen
  // only what the pill KNOWS changes, never how it looks. All three hooks read
  // stores and the pathname alone, so they are provider-free on every route,
  // and the lock and the controls are handed the task in VIEW, never left to
  // read the store's selection: a selection outlives its page, and off a task's
  // screen (null) a task the person has left must never re-render the pill.
  // Everything that needs more lives in pill-task/ leaves mounted in task mode
  // only. The lock is a FRESH object every render: its fields are read one by
  // one, and the object never enters a dependency array or a prop.
  const task = usePillTaskMode();
  const lock = usePillTaskLock(task.taskId);
  const controls = useTaskScreenControlsFor(task.taskId);
  const pickersRef = useRef<PillTaskPickersHandle>(null);
  const paneOpen = useComputerStore((s) =>
    task.taskId ? s.isOpen(task.taskId) : false
  );
  const closeDoor = useLiveStore((s) => s.closeDoor);
  const homeDraft = useComposerDraftStore((s) => s.homeDraft);
  const parkHomeDraft = useComposerDraftStore((s) => s.parkHomeDraft);
  const [localText, setLocalText] = useState("");
  const text = liveOn ? homeDraft : localText;
  const setText = useCallback(
    (t: string) => (liveOn ? parkHomeDraft(t) : setLocalText(t)),
    [liveOn, parkHomeDraft]
  );
  const [focused, setFocused] = useState(false);
  const [typed, setTyped] = useState("");
  // An empty wallet (founder 09-02): the pill says so and its key becomes the
  // door to top up, so a send is never offered that the server would refuse.
  // Fail-open while the balance is not loaded (a cache miss is not zero).
  const outOfCredits = useCreditStore((s) => s.isLoaded && s.balance <= 0);
  const rootRef = useRef<HTMLDivElement>(null);
  const live = focused || text.length > 0;

  // ── the rich editor: the Dock's editor, moved here with the compact pill's
  // retirement (spec 2026-09-12 one-composer). The box is already the two
  // rows the Dock's multi-line grid morphed into (the text line, then the
  // control row), so nothing of that grid moves; what moves is the editor,
  // the height animator (every growth rides ONE interruption-safe tween) and
  // the multi state, which here aligns the mark with the first line.
  const richRef = useRef<RichEditorHandle>(null);
  const [richMulti, setRichMulti] = useState(false);
  // The animator's host is the composer surface alone (the editor row and
  // the control row, surfaceRef), never the card: the card also holds the
  // deck, which grows on its own 320 ms grid-rows rail as an answer streams
  // or the history opens, and the editor animates its host on every
  // keystroke from the last height it recorded, so a card host would tween
  // the whole card back to its pre-stream size on the first key typed into
  // a streaming answer (review 1, I1). One animator for the box's life; it
  // holds the ref OBJECT and reads .current only at animate time (handlers),
  // never during render.
  const surfaceRef = useRef<HTMLDivElement>(null);
  // The rule cannot see that through the call, and it read nothing here while
  // the receipt effect's exhaustive-deps suppression (now usePillReceipt.ts)
  // stood in this file: a react-hooks suppression skips the whole component.
  // eslint-disable-next-line react-hooks/refs
  const heightAnimator = useMemo(() => createHeightAnimator(surfaceRef), []);

  // Starters hand their ask through the draft store's home lane; focus follows.
  const homePrefill = useComposerDraftStore((s) => s.homePrefill);
  const consumeHomePrefill = useComposerDraftStore((s) => s.consumeHomePrefill);
  useEffect(() => {
    if (!homePrefill || !settled) return;
    // Store event -> local draft, take-once: the dock's prefill law verbatim.
    // One render settles it; the store row is cleared below.
    setText(homePrefill);
    consumeHomePrefill();
    // The editor is uncontrolled: the words go in through its handle.
    richRef.current?.setText(homePrefill);
    richRef.current?.focus();
  }, [homePrefill, consumeHomePrefill, setText, settled]);

  // The door opened on this mount (the Dock hosts the box only while it is
  // open): take focus with the draft in place, the caret at its end, the way
  // the Dock's pill did when its door opened. The rich editor remounts
  // empty, so the parked draft goes back in through its handle first (its
  // setText lands the caret at the end).
  useEffect(() => {
    if (!liveOn || !settled) return;
    const draft = useComposerDraftStore.getState().homeDraft;
    if (draft) richRef.current?.setText(draft);
    richRef.current?.focus();
  }, [liveOn, settled]);

  const sendMessage = useTaskStore((s) => s.sendMessage);
  const preWarm = useComposerPrewarm();
  const port = usePillEditorPort({ richRef, text, setText });
  // The pill's files (usePillFiles.ts): the shared attach lane, drop and paste,
  // and the parking of unsent Files across the type door.
  const {
    pendingAttachments,
    imageError,
    openFilePicker,
    pickFiles,
    resetAttachments,
    removeAttachment,
    isDragOver,
    dragHandlers,
    onPaste,
  } = usePillFiles(liveOn, settled, rootRef);

  const classicSend = useCallback(
    (t: string, i?: ImageAttachment[], f?: FileAttachment[]) => {
      sendMessage(t, i, f);
      // On a task route the pill IS that screen's composer: the words ride into
      // the task in view (or the pending one the header's Computer allocated),
      // and the task layout already owns the URL. Nobody is sent anywhere.
      if (!task.route) router.push("/task");
    },
    [sendMessage, router, task.route]
  );
  // The Line: while it is open a typed ask rides it (spoken and shown), the
  // Dock's own wiring. The hook's state is module-scope and shared, so this
  // second mount arms nothing twice: its effect only primes the mic choice.
  const { line, typed: typedViaLine } = useLine(liveOn);
  const composer = useComposerConverse({
    classicSend,
    viaLine: liveOn ? typedViaLine : undefined,
    // On a task route the pill takes the task box's own contract: a send with a
    // task selected is mid-thread (the classic rail, or the door's verdict),
    // and the door's controls are the screen's own. Off it, or with the flag
    // off, both are exactly as absent as they were.
    isEligible: () => !(task.route && useTaskStore.getState().selectedTaskId),
    onControl: task.route ? controls.onDoorControl : undefined,
  });
  const composerSubmit = composer.submit;
  // Agents first (train 1b, spec §5.3): the pill follows the ONE app-wide
  // pick. The flag is read at call time, so with AGENTS_V1 off, or with
  // nobody picked, every string and the face below are the ones they were.
  const picked = useTeamStore(selectSelectedAgent);
  const selectAgent = useTeamStore((s) => s.select);
  // AGENTS_V1 draws the team; the converse lane, always on since the compact
  // pill retired, is the one lane that carries an ask to an agent (rule D).
  const agent = isAgentsV1() ? picked : null;
  const speak = agent ? `Message ${agent.name}` : null;
  const askLabel = speak ?? "Ask Bash anything";
  const keepLabel = agent
    ? `Keep talking to ${agent.name}`
    : "Keep talking to Bash";
  // The row's name is who ANSWERED: the frame's word beats the pick, so a
  // turn the lead took for a paused agent says Bash and the note says why.
  const speaker =
    composer.answeredBy?.name ??
    (agent && !composer.note ? agent.name : "Bash");

  // The thread (founder 09-01: "the bash icon should go on top even for
  // responses"): the pill grows upward to hold your line, Bash's row and the
  // answer. The folded thread keeps its last content for one transition so it
  // can close instead of vanishing.
  const threadOpen = composer.ui.phase !== "idle";
  const busy =
    composer.ui.phase === "thinking" ||
    composer.ui.phase === "routing" ||
    composer.ui.phase === "answering";
  const [heldUi, setHeldUi] = useState<ConverseUiState | null>(null);
  useEffect(() => {
    if (threadOpen) {
      // Store event -> local hold, the same effect-write the prefill uses.
      setHeldUi(composer.ui);
      return;
    }
    const t = window.setTimeout(() => setHeldUi(null), THREAD_MS);
    return () => window.clearTimeout(t);
  }, [threadOpen, composer.ui]);
  const shownUi = threadOpen ? composer.ui : heldUi;

  const composerDismiss = composer.dismiss;
  // The task receipt's choreography (usePillReceipt.ts): show, lift, fly, fold.
  const receiptStage = usePillReceipt(composer.ui, composerDismiss, rootRef);
  // The panels (composer panels, spec 2026-09-19 §3.4): a history or tray
  // panel is a door (pillPanels.ts); a sheet or a receipt is a tenant of the
  // deck below. Opening a source folds the thread, as the receipt row does.
  usePillPanelDoors(composer.ui.panels, composerDismiss);
  // The deck reads the LIVE ui, never the held copy: × has to clear the panel
  // in the state closePanel mutates, and a panel only ever rides an open
  // thread, so the held copy would draw one nothing could close.
  const panels = composer.ui.panels;
  const closePanel = composer.closePanel;
  const openFromPanel = useCallback(
    (route: string) => {
      router.push(route);
      composerDismiss();
    },
    [router, composerDismiss]
  );
  const receiptLeaving = receiptStage === "lift";

  // The history (founder 09-01: "add an up arrow on the right end to see the
  // history here"): the box grows upward to show the composer's past
  // conversation; a send or Esc closes it, the converse thread outranks it.
  // Open or closed lives in the live store, one app-wide like the door (spec
  // 2026-09-12 one-composer): the Dock hears the ⌃⌃ pair (one clock with the
  // single tap's line toggle) and toggles it there; the arrow here does the
  // same. A level, not an edge: it lives only while the door is open (the
  // store's closeDoor drops it) and the dock is visible (the Dock's half);
  // never an unmount cleanup here, which Strict Mode runs on mount and would
  // shut a history the pair had just opened (review 1, I2).
  const historyOpen = useLiveStore((s) => s.historyOpen);
  const historyOpenedAt = useLiveStore((s) => s.historyOpenedAt);
  const toggleHistory = useLiveStore((s) => s.toggleHistory);
  const closeHistory = useLiveStore((s) => s.closeHistory);
  const historyUp = historyOpen && !threadOpen;
  useEffect(() => {
    if (!historyUp) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // The shell's dock window hands the keyboard back on an unclaimed
      // Escape; this one folded the history, so it is claimed (spec §4).
      if (isDockWindow()) e.preventDefault();
      closeHistory();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [historyUp, closeHistory]);

  // The self-typing example: runs while the field is idle, stops on focus and
  // while the thread is open (the field then invites a follow-up instead); on
  // a task's screen the field says the task's words, never an example. It
  // stops while the history is up too: each typed letter re-rendered the
  // whole pill, history and all, under the reader (spec 2026-09-24 §1). With
  // an agent picked the field says whom it messages instead (train 1b).
  useEffect(() => {
    if (live || threadOpen || historyUp || task.on || agent) {
      setTyped("");
      return;
    }
    if (
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      // The media query can only be read after mount (window is absent during
      // SSR), so the static fallback HAS to arrive as a post-mount state write.
      setTyped(EXAMPLE_PROMPTS[0]);
      return;
    }
    const loop = createTypingLoop({
      prompts: EXAMPLE_PROMPTS,
      onText: setTyped,
    });
    loop.start();
    return () => loop.stop();
  }, [live, threadOpen, historyUp, task.on, agent]);

  // One Bash, two seats. The face is absolute in the pill and is translated to
  // whichever seat the state leaves for him: the input line, or the top of the
  // thread. A seat change waits for the thread to grow, then he flies, hops
  // and ripples on landing.
  const seat = threadOpen && !receiptLeaving ? "top" : "line";
  const flyRef = useRef<HTMLSpanElement>(null);
  usePillSeat(rootRef, flyRef, seat, composer.ui.phase, receiptStage);

  // The dock's focus law: a lingering selection would route the send INTO that task.
  const handleFocus = useCallback(() => {
    setFocused(true);
    // The focus law clears a lingering selection so a send never lands INTO a
    // task; on a task route the selection is the subject (R-F3).
    if (task.route) return;
    const store = useTaskStore.getState();
    if (store.selectedTaskId) {
      const draftLevel = store.nextTaskAccessLevel;
      store.createNewTask();
      if (draftLevel) store.setNextTaskAccessLevel(draftLevel);
    }
  }, [task.route]);

  // What a send reads of task mode, named so the callback's dependencies are
  // plain values (the lock itself is a fresh object every render).
  const { on: taskOn, route: taskRoute } = task;
  const { blocked: lockBlocked, addressToLane } = lock;
  const send = useCallback(
    (source: "enter" | "button" | "voice" | "retry", override?: string) => {
      // A typed send waits for its files (spec 2026-09-24, Part B: the task
      // box's hasProcessing rule). A voice final or a retry carries no files,
      // so it is never held (dock law: "never touch the input").
      if (
        !override &&
        pendingAttachments.some((a) => a.status === "processing")
      )
        return;
      let trimmed = (override ?? text).trim();
      // A voice final never carries the typed draft's attachments (dock law: "never touch the input").
      const complete = override
        ? []
        : pendingAttachments.filter((a) => a.status === "complete");
      if (!trimmed && complete.length === 0) return;
      if (taskRoute && !override) {
        // Shut exactly when the task box was shut. The words stay.
        if (taskOn && lockBlocked) return;
        // An empty wallet shuts every send that would ride the CLASSIC rail,
        // where a refusal is a failed bubble with a retry the server refuses
        // again: a task selected on this route, the pending placeholder the
        // header's Computer allocated included (the same read isEligible
        // makes). With no task selected the composer takes it, and says so.
        if (outOfCredits && useTaskStore.getState().selectedTaskId) return;
        // The box's own order: a bare /skill expands, on the new-task page too
        // (R-F14: "Create with Bash" prefills one, and only the web expands it),
        // then the lane in view is addressed exactly once (R5), an
        // attachment-only send included.
        if (trimmed)
          trimmed = pickersRef.current?.expandSlash(trimmed) ?? trimmed;
        if (taskOn) trimmed = addressToLane(trimmed);
      }
      closeHistory();
      const { images, files } = rowsToAttachments(complete);
      // Analytics v1: no pill-level send event (home:dock_send is retired). The
      // lane that carries the ask counts it once as delegation:submitted — the
      // composer gateway in useComposerConverse, the classic rail in sendMessage.
      composerSubmit(
        trimmed,
        images.length > 0 ? images : undefined,
        files.length > 0 ? files : undefined
      );
      if (!override) {
        setText("");
        richRef.current?.clear();
        resetAttachments();
        // A click on the key took the focus, and the key then disables (or
        // turns into Stop), dropping it on the body: the next words needed a
        // second click on the field (Ankur, #bugs 09-19). Enter never left it.
        richRef.current?.focus();
      }
    },
    [
      text,
      setText,
      pendingAttachments,
      composerSubmit,
      resetAttachments,
      closeHistory,
      taskOn,
      taskRoute,
      lockBlocked,
      outOfCredits,
      addressToLane,
    ]
  );

  // Try again on a failed ask in the history: the words go out as a new ask
  // through the same send (the history closes first); the draft stays. On a
  // task's screen a send would land in the task in view, so the words go into
  // the field instead, focused, for him to send (the review's ruling).
  const retryFromHistory = useCallback(
    (words: string) => {
      if (!taskOn) return send("retry", words);
      closeHistory();
      setText(words);
      richRef.current?.setText(words);
      richRef.current?.focus();
    },
    [send, taskOn, closeHistory, setText]
  );

  // Part B (spec 2026-09-24): the key is shut while any file uploads, reads
  // Uploading, and names the file it waits for.
  const uploading = pendingAttachments.find((a) => a.status === "processing");
  const waitReason = uploading
    ? waitingForUpload(uploading.filename)
    : undefined;
  const canSend = useMemo(
    () =>
      !uploading &&
      (text.trim().length > 0 ||
        pendingAttachments.some((a) => a.status === "complete")),
    [text, pendingAttachments, uploading]
  );

  const open = threadOpen || historyUp;
  // Escape with a turn in flight is the task screen's key, never the door's:
  // the layout's Escape stops the MAIN turn (with only a helper's lane in
  // flight it does nothing, as under the old box; the key's Stop stops that
  // lane). Either way the door never shuts over running work.
  const turnHasTheKey = task.on && controls.inFlight;
  return (
    <section
      className="bh-hero text-3.5 leading-normal text-fg-default"
      aria-label={speak ?? "Ask Bash"}
    >
      <div
        ref={rootRef}
        className={cn(
          // the pill's backdrop-filter makes it a stacking context, so
          // without a z-index of its own it (deck and all) paints under the
          // Needs-you glass cards that follow it in the DOM — z-1 clears
          // every z-auto sibling
          "bh-pill transition-bh-pill-shadow relative z-1 rounded-[22px] bg-bg-elevated-90 shadow-bh-pill backdrop-blur-lg backdrop-saturate-150 focus-within:shadow-bh-pill-focus",
          live && "bh-pill--live",
          open && "bh-pill--open",
          historyUp && "bh-pill--history",
          busy && "bh-pill--busy",
          // A DRAWN panel holds Escape in every phase (PanelDeck), busy or
          // not, so a host that yields the key to a closable pill can see the
          // deck's claim too.
          deckDraws(panels) && "bh-pill--panel"
        )}
        data-testid="bh-pill"
        data-composer-bar
        onDragEnter={dragHandlers.onDragEnter}
        onDragLeave={dragHandlers.onDragLeave}
        onDragOver={dragHandlers.onDragOver}
        onDrop={dragHandlers.onDrop}
      >
        {isDragOver && (
          <div className="bh-pill__drop pointer-events-none absolute inset-0 z-2 flex items-center justify-center rounded-[22px] border-2 border-dashed border-gold-molten-mid-40 bg-gold-molten-hi-8 text-3.5 font-medium text-gold-ink">
            Drop files to attach
          </div>
        )}
        {/* The task's next-prompt suggestion, as the pill's own top row
            (founder 08-30: "not a separate box"). Handed the task in VIEW,
            never the raw selection; outside the surface, whose height tweens. */}
        <PillTryRow taskId={task.taskId} />
        <AttachmentStrip
          pendingAttachments={pendingAttachments}
          imageError={imageError}
          onRemove={removeAttachment}
          filenameMaxWidth={200}
        />
        {/* The queued notes of a running turn (the task box's own shelf). */}
        {task.on && <StagedQueue />}
        <div
          className={cn(
            "bh-thread-wrap transition-bh-thread-rows grid",
            open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
          )}
        >
          {/* The padding rides what the deck DRAWS, not what it holds: a kind
              no host draws (Train 2's connect and confirm) would otherwise
              leave an empty 12 px box above the thread. */}
          {deckDraws(panels) && (
            <div className="px-3 pt-3" data-testid="bh-panels">
              <PanelDeck
                panels={panels}
                onClose={closePanel}
                onOpen={openFromPanel}
                onUndo={undoRunApi}
              />
            </div>
          )}
          {shownUi && (
            <PillThread
              ui={shownUi}
              onDismiss={composer.dismiss}
              onRetryClassic={composer.retryClassic}
              onUpgrade={() => router.push("/account/plan")}
              lift={receiptStage === "lift"}
              speaker={speaker}
            />
          )}
          {shownUi && composer.note && (
            <p
              className="bh-note pt-0 pr-2 pb-2 pl-9 text-3 text-fg-subtle"
              role="status"
            >
              {composer.note}
            </p>
          )}
        </div>
        {/* A popover now (founder 09-01's "grows upward" as a float above the
            box, composer/Island.tsx's own pattern): toggleHistory stamps
            every open; the fallback only types it. Always mounted so a close
            animates out; the card inside mounts fresh only while open. */}
        <PillHistory
          open={historyUp}
          anchorRef={rootRef}
          onClose={closeHistory}
          onRetry={retryFromHistory}
          now={historyOpenedAt ?? 0}
          agent={agent}
        />
        {/* The composer surface, the height animator's host: the editor row
            and the control row; the deck above stays out of its tween. */}
        <div
          ref={surfaceRef}
          className="bh-pill__surface"
          data-testid="bh-pill-surface"
        >
          <div
            className={cn(
              "bh-pill__line flex min-h-16 items-center gap-2.5 pt-2.5 pb-0.5 px-3 sm:gap-3.5 sm:pt-3 sm:pr-4.5 sm:pb-1 sm:pl-4",
              // Only the thread tightens the line (the face flies to the
              // thread's top seat); the history floats above and leaves the
              // face, and so the text, where they were.
              threadOpen && "pt-1.5 pb-0.5 sm:pt-1.5 sm:pb-0.5",
              // The rich editor past one line: the mark rides the first line,
              // not the middle of the block (the Dock's multi state aligned its
              // input to the start the same way).
              richMulti && "items-start"
            )}
          >
            {/* Bash's seat at the input; the face itself flies (below). */}
            <span
              className={cn(
                "bh-seat bh-pill__face inline-block h-8 w-8 flex-none",
                threadOpen && "w-0"
              )}
              data-seat="line"
              aria-hidden="true"
            />
            <div
              className={cn(
                "bh-field relative min-h-[1.5em] min-w-0 flex-1 items-baseline text-4 leading-[1.4] text-fg-default flex flex-wrap overflow-visible whitespace-normal",
                threadOpen && "pl-7.5"
              )}
              onClick={() => richRef.current?.focus()}
            >
              <span
                className={cn("bh-typed text-fg-subtle", live && "hidden")}
                data-testid="bh-typed"
              >
                {task.on ? (
                  lock.placeholder
                ) : threadOpen ? (
                  keepLabel
                ) : (
                  <>
                    {speak ?? typed}
                    <i
                      className="bh-caret animate-bh-blink ml-0.5 inline-block h-[1.15em] w-0.5 translate-y-[0.15em] bg-gold-molten-mid motion-reduce:opacity-100"
                      aria-hidden="true"
                    />
                  </>
                )}
              </span>
              <RichEditor
                ref={richRef}
                className={cn(
                  "bh-real absolute inset-0 m-0 w-full py-0 min-h-[1.5em] text-4 leading-[1.4] opacity-0",
                  live && "static opacity-100",
                  open && "[&.is-empty::before]:text-fg-faint"
                )}
                placeholder={
                  outOfCredits
                    ? "Out of credits, upgrade to continue"
                    : task.on
                      ? lock.placeholder
                      : threadOpen
                        ? keepLabel
                        : ""
                }
                ariaLabel={askLabel}
                heightAnimator={heightAnimator}
                onTextChange={(t) => {
                  setText(t);
                  // On a task route the pickers leaf warms (THIS task, or
                  // the pending new-task id, as the old box did).
                  if (t.length > 0 && !task.route) preWarm();
                }}
                onMultiChange={(m) => {
                  // The morph edge: the class flips inside the animator's
                  // mutate, which owns this round's height tween.
                  heightAnimator.animate(() =>
                    flushSync(() => setRichMulti(m))
                  );
                }}
                onSend={() => send("enter")}
                onKeyDown={(e) => {
                  // The Line: Escape from an empty editor shuts the door
                  // (the mark is the other way back); words, an answer or
                  // the history keep it open (the history takes its own
                  // Escape). The textarea carries the same rule below.
                  if (
                    liveOn &&
                    e.key === "Escape" &&
                    !text &&
                    !open &&
                    !turnHasTheKey
                  )
                    closeDoor();
                }}
                onFocus={handleFocus}
                onBlur={() => setFocused(false)}
                onPasteFiles={onPaste}
              />
              {/* On the whole task route, the new-task page included (R-F14):
                  the lists and the slash expansion were the old box's there. */}
              {task.route && (
                <PillTaskPickers
                  ref={pickersRef}
                  taskId={task.taskId}
                  text={text}
                  focused={focused}
                  laneInView={lock.laneInView}
                  port={port}
                />
              )}
            </div>
            {/* The up arrow at the right end opens the composer's history above (founder 09-01). */}
            {!threadOpen && (
              <Button
                variant="icon"
                size="icon-sm"
                type="button"
                className="bh-pill__hist rounded-full"
                aria-label={historyUp ? "Close history" : "History"}
                aria-expanded={historyUp}
                title="History"
                onClick={toggleHistory}
              >
                <ChevronLeftIcon
                  className={cn(
                    "transition-bh-rotate h-3.5 w-3.5 rotate-90",
                    historyUp && "rotate-270"
                  )}
                />
              </Button>
            )}
          </div>
          <div className="bh-pill__row flex items-center gap-1 pt-0.5 pb-2.5 px-2 sm:gap-1.5 sm:pt-1 sm:pr-3 sm:pb-3 sm:pl-3.5">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-md"
                    type="button"
                    className="bh-plus"
                    aria-label="Attach files"
                    onClick={() => openFilePicker(pickFiles)}
                  >
                    <PlusIcon className="w-4.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" sideOffset={6}>
                  Attach file
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <span className={PILL_CHIP_CLASS}>
              <ModelSelector forNewTask={!task.on} compact={paneOpen} />
            </span>
            <span className={PILL_CHIP_CLASS}>
              <TaskAccessLevelPicker taskId={task.taskId} compact={paneOpen} />
            </span>
            {agent && (
              <button
                type="button"
                className="bh-back transition-bh-hover inline-flex h-7.5 flex-none items-center rounded-full border border-border-default bg-bg-elevated px-2 text-3.5 text-fg-muted hover:bg-bg-muted hover:text-fg-default sm:px-2.5"
                aria-label="Back to Bash"
                onClick={() => selectAgent(null)}
              >
                Back to Bash
              </button>
            )}
            <span className="bh-spacer flex-1" />
            {/* A turn in flight outranks an empty wallet (R-F10, and the task
                box's own order): Stop is always one click away. */}
            {outOfCredits && !turnHasTheKey ? (
              <Button
                size="sm"
                type="button"
                className="bh-key px-2.5"
                aria-label="Upgrade"
                onClick={() => router.push("/account/plan")}
              >
                Upgrade
              </Button>
            ) : task.on ? (
              // The same key in the same seat; only what it reads changes.
              // hasDraft = something to send: words, or a finished file alone.
              // An empty wallet shuts the Queue, as it shut the box's.
              <PillTaskKey
                inFlight={controls.inFlight}
                hasDraft={canSend}
                waitReason={waitReason}
                blocked={lock.blocked || outOfCredits}
                reason={lock.reason}
                deployPaused={lock.deployPaused}
                offline={lock.offline}
                onSend={() => send("button")}
                onStop={controls.stopInView}
              />
            ) : (
              <Button
                size="sm"
                type="button"
                className={PILL_KEY_CLASS}
                aria-label={waitReason ?? "Delegate"}
                title={waitReason}
                disabled={!canSend}
                onClick={() => send("button")}
              >
                <DelegateFace waitReason={waitReason} />
              </Button>
            )}
          </div>
        </div>
        {/* One Bash: absolute in the pill, flown to his seat by the effect
            above. With the Line on he sits inside the ball, the mark: the
            Dock's own (Dock.tsx), a click is the way back to voice. While the
            line is off the pill is the classic composer, so the ball wears
            the thread's mood; on, the line's. The click is ignored while an
            answer streams in the thread: closing the door unmounts the pill
            and would abort the stream (Escape's own guard is stricter: an
            empty editor with neither a thread nor the history up). */}
        <span
          ref={flyRef}
          className="bh-pill__fly transition-bh-fly absolute top-0 left-0 z-3 translate-0 motion-reduce:transition-none"
        >
          {liveOn ? (
            <Ball
              size={MARK_SIZE}
              mood={
                line.phase === "off"
                  ? moodFor(shownUi ?? { phase: "idle" })
                  : faceMoodFor(line)
              }
              tone={toneFor(line)}
              bashRef={liveBashRef}
              talkRef={liveTalkRef}
              className="bh-pill__mark cl-orb--mark focus-visible:outline-offset-2"
              label="Back to voice"
              onClick={() => {
                if (!busy) closeDoor();
              }}
            />
          ) : agent ? (
            <AgentFace
              agent={agent}
              size="desk"
              mood={moodFor(shownUi ?? { phase: "idle" })}
            />
          ) : (
            <BashFace size="sm" mood={moodFor(shownUi ?? { phase: "idle" })} />
          )}
        </span>
      </div>
    </section>
  );
}
